import { rem } from 'polished'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import LabelGroup from 'LuxKit/Label/LabelGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import { AccommodationPackageOptionTileVisualStyle } from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTile'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'

import PropertyCheapestRatePriceDetails from './PropertyCheapestRatePriceDetails'
import PropertyPromotionDetails from './PropertyPromotionDetails'
import PropertyPromotionLabel from './PropertyPromotionLabel'
import PropertyRateInclusions from './PropertyRateInclusions'
import { isPropertyInclusionValidForDuration } from './singlePropertyUtils'

export const AnytimeRateOptionTile = styled.div`
  ${AccommodationPackageOptionTileVisualStyle}

  display: grid;
  gap: ${rem(12)} ${rem(32)};
  grid-template-columns: 1fr;
  grid-auto-rows: min-content;

  ${mediaQueryUp.tablet} {
    gap: ${rem(12)} ${rem(16)};
  }

  ${mediaQueryUp.desktop} {
    gap: ${rem(12)} ${rem(32)};
    grid-template-columns: ${rem(238)} 1fr ${rem(285)};
  }
`

const AnytimeRateOptionTileDetails = styled.div`
  ${mediaQueryUp.desktop} {
    &:last-child {
      grid-column: span 2;
    }
  }
`

interface Props {
  property: App.Property.Property
  propertyRoom: App.Property.Room
  anytimeRoomRate: App.Property.AnytimeRoomRate | undefined
  duration: number
  showSelectDatesPrompt?: boolean
}

export function PropertyAnytimeRateOptionContent({
  property,
  propertyRoom,
  anytimeRoomRate,
  duration,
  showSelectDatesPrompt = false,
}: Props) {
  const intl = useIntl()
  const rateContent = useMemo(() => {
    const { promotion, inclusions, bonusInclusions, luxPlusInclusions, bundleInclusions } = propertyRoom
    return {
      promotion: promotion && duration >= promotion.minimumLos ? promotion : undefined,
      inclusions: inclusions.filter(inclusion => isPropertyInclusionValidForDuration(inclusion, duration)),
      bonusInclusions: bonusInclusions.filter(inclusion => isPropertyInclusionValidForDuration(inclusion, duration)),
      luxPlusInclusions: luxPlusInclusions.filter(inclusion => isPropertyInclusionValidForDuration(inclusion, duration)),
      bundleInclusions: bundleInclusions.filter(inclusion => isPropertyInclusionValidForDuration(inclusion, duration)),
    }
  }, [propertyRoom, duration])
  const { promotion, inclusions, bonusInclusions, luxPlusInclusions, bundleInclusions } = rateContent

  if (!promotion && !inclusions.length && !bonusInclusions.length && !luxPlusInclusions.length && !bundleInclusions.length) return null

  return <AnytimeRateOptionTile>
    <VerticalSpacer gap={12}>
      <VerticalSpacer gap={4}>
        {promotion && <LabelGroup>
          <PropertyPromotionLabel promotion={promotion} />
        </LabelGroup>}
        {showSelectDatesPrompt && <BodyText
          variant="large"
          weight="bold"
          colour="normal-contrast"
        >
          {intl.formatMessage({
            id: 'Q81wyQpQgKNK8UxtYyU6',
            defaultMessage: 'Select dates to unlock package options',
            description: 'Placeholder text when dates aren’t selected for property anytime rate option',
          })}
        </BodyText>}
      </VerticalSpacer>
      {promotion && <PropertyPromotionDetails promotion={promotion} />}
    </VerticalSpacer>
    <AnytimeRateOptionTileDetails>
      <PropertyRateInclusions
        property={property}
        inclusions={inclusions}
        bonusInclusions={bonusInclusions}
        luxPlusInclusions={luxPlusInclusions}
        bundleInclusions={bundleInclusions}
        maxHeight={208}
      />
    </AnytimeRateOptionTileDetails>
    {(anytimeRoomRate?.duration === duration) && <div>
      <PropertyCheapestRatePriceDetails
        hotelOnlyRate={anytimeRoomRate}
        hotelWithFlightsRate={undefined}
        horizontalAlign="start"
      />
    </div>}
  </AnytimeRateOptionTile>
}

export default withOfferPageAndReduxState((state, appState, ownProps) => ({
  property: state.property!, // Guaranteed by `<PropertyContainer/>`.
  anytimeRoomRate: appState.offer.propertyAnytimeRates[state.property!.id]?.anytimeRoomRates?.[ownProps.propertyRoom.id],
  duration: state.duration,
  showSelectDatesPrompt: true,
  open: !!state.activeFilter,
}), PropertyAnytimeRateOptionContent, preventRenderWithOpenFilter)
